import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, type MeResponse } from '../../api/client'
import { getPieceMetrics } from '../api'
import { describeError } from '../../lib/describeError'
import {
  bothLabel, byDayLabel, channelLabel, countingSince, dayLabel, deskTodayText, deskWho, deskWindowText, emptyText, etToday, fillDays, formatDuration, groupBar, membersGainedText, noteOf, NO_SEARCH_TEXT, pct, pctOrDash, privateReadersNote, rateText, TYPICAL_TIME_HINT, WINDOWS, windowEmpty,
  type ArticleMetrics, type DayViews, type DeskReaders, type PieceCopy, type PieceMetrics, type Window,
} from '../readers'
import { focus } from './ui'

const H3 = 'm-0 font-ui text-[13px] font-semibold text-paper'
const CARD = 'flex flex-col gap-2 border-t border-ink-500 py-3'
const DESK_SEG_COLOR = { anonymous: 'bg-amber-glow', known: 'bg-radar-cyan', member: 'bg-radar-violet' } as const
const SEG_COLOR = { member: 'bg-radar-violet', signed_in: 'bg-radar-cyan', anonymous: 'bg-amber-glow', not_recorded: 'bg-ink-400' } as const

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex min-w-[72px] flex-col">
      <span className="font-serif text-[24px] leading-none text-paper">{value}</span>
      <span className="text-[12px] text-paper-faint">{label}</span>
    </div>
  )
}

function Switch<T extends string | number>({ label, items, value, onChange }: {
  label: string; items: { key: T; label: string }[]; value: T; onChange: (k: T) => void
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1">
      {items.map((i) => (
        <button key={String(i.key)} type="button" aria-pressed={value === i.key} onClick={() => onChange(i.key)}
                className={`min-h-9 rounded-lg border px-3 text-[13px] font-semibold ${focus} ${
                  value === i.key ? 'border-amber-glow text-paper' : 'border-ink-400 text-paper-dim hover:text-paper'}`}>
          {i.label}
        </button>
      ))}
    </div>
  )
}

/** One bar per ET day, as SVG; the summary is in the aria-label. */
function DayChart({ days: sent, windowDays, since, exact }: { days: DayViews[]; windowDays: number; since: string | null; exact?: boolean }) {
  if (sent.length === 0) return null
  // the desk's days are already one per whole day of its window: no filling out to today
  const days = exact ? sent : fillDays(sent, windowDays, since, etToday())
  const max = Math.max(1, ...days.map((d) => d.views))
  const W = 100
  const gap = 0.6
  const bw = W / days.length
  return (
    <svg role="img" aria-label={byDayLabel(days)} viewBox={`0 0 ${W} 40`} preserveAspectRatio="none" className="h-[72px] w-full text-radar-cyan">
      {days.map((d, i) => {
        const h = (d.views / max) * 38
        return <rect key={d.date} x={i * bw + gap / 2} y={40 - h} width={Math.max(0.1, bw - gap)} height={h} fill="currentColor"><title>{`${d.views} on ${dayLabel(d.date)}`}</title></rect>
      })}
    </svg>
  )
}

function DeskCards({ d, m, isMembers, windowDays, canOpen }: { d: DeskReaders; m: ArticleMetrics; isMembers: boolean; windowDays: number; canOpen: boolean }) {
  const t = d.totals
  const bar = deskWho(t.who)
  const days: DayViews[] = (d.daily ?? []).map((x) => ({ date: x.day.slice(0, 10), views: x.views }))
  const sources = d.sources ?? []
  return (
    <>
      <section aria-label="Readers" className={CARD}>
        <h3 className={H3}>Readers</h3>
        <div className="flex gap-5"><Stat label="People" value={t.people} /><Stat label="Views" value={t.views} /></div>
        <DayChart days={days} windowDays={windowDays} since={null} exact />
        <p className="m-0 text-[12px] text-paper-faint">{deskWindowText(d.window)}</p>
        <p className="m-0 text-[12px] text-paper-faint">{deskTodayText(d.today)}</p>
        <p className="m-0 text-[12px] text-paper-faint">People are counted once per day, as on the Visitors Desk.</p>
        {canOpen && <Link to={d.href} className={`font-ui text-[13px] font-semibold text-amber-glow underline underline-offset-2 ${focus}`}>Open in the Visitors Desk →</Link>}
      </section>
      <section aria-label="Who reads it" className={CARD}>
        <h3 className={H3}>Who reads it</h3>
        {bar.length > 0 && (
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-ink-600" aria-hidden="true">
            {bar.map((s) => <span key={s.key} className={DESK_SEG_COLOR[s.key]} style={{ width: `${s.pct}%` }} />)}
          </div>
        )}
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-paper-dim">
          {bar.length === 0 && <li>Nobody yet.</li>}
          {bar.map((s) => (
            <li key={s.key} className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 flex-none rounded-sm ${DESK_SEG_COLOR[s.key]}`} aria-hidden="true" />
              {s.label}: {s.count} ({s.pct}%)
            </li>
          ))}
        </ul>
      </section>
      <section aria-label="How well it reads" className={CARD}>
        <h3 className={H3}>How well it reads</h3>
        <div className="flex flex-wrap gap-5">
          <Stat label="Real reads" value={t.real_reads} />
          <Stat label="Read to the end" value={rateText(t.read_end_rate, t.real_reads)} />
          <Stat label="Read another page" value={rateText(t.recirculation_rate, t.real_reads)} />
        </div>
        <h4 className="m-0 mt-1 font-ui text-[12px] font-semibold text-paper-dim">Reading time (last {d.window.days ?? windowDays} days, live)</h4>
        <div className="flex flex-wrap gap-5">
          <Stat label="Read half" value={pctOrDash(m.read_half_pct)} />
          <Stat label="Typical time" value={formatDuration(m.median_seconds)} />
        </div>
        <p className="m-0 text-[12px] text-paper-faint">{TYPICAL_TIME_HINT}</p>
      </section>
      <section aria-label="Access and conversion" className={CARD}>
        <h3 className={H3}>Access &amp; conversion</h3>
        <div className="flex flex-wrap gap-5">
          {isMembers && <Stat label="Hit paywall" value={t.paywall_hits} />}
          <Stat label="Arrived here first" value={t.entries} />
          <Stat label="Members gained" value={membersGainedText(t.members_gained)} />
        </div>
      </section>
      <section aria-label="Where readers came from" className={CARD}>
        <h3 className={H3}>Where readers came from</h3>
        {sources.length === 0 && <p className="m-0 text-[13px] text-paper-dim">No visits recorded.</p>}
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-paper-dim">
          {sources.map((c) => <li key={c.channel} className="flex justify-between gap-3"><span className="min-w-0 break-words">{c.label}</span><span>{c.visits}</span></li>)}
        </ul>
      </section>
    </>
  )
}

function Cards({ copy, many, windowDays, since, quiet, canOpen }: { copy: PieceCopy; many: boolean; windowDays: number; since: string | null; quiet: boolean; canOpen: boolean }) {
  const m: ArticleMetrics = copy.metrics
  const bar = groupBar(m.access)
  const isMembers = copy.where === 'members'
  const showFull = !isMembers && many
  const privateNote = privateReadersNote(m.private_readers)
  const desk = m.desk ?? null
  return (
    <>
      {!quiet && desk && <DeskCards d={desk} m={m} isMembers={isMembers} windowDays={windowDays} canOpen={canOpen} />}
      {!quiet && !desk && <>
      <section aria-label="Readers" className={CARD}>
        <h3 className={H3}>Readers</h3>
        <div className="flex gap-5"><Stat label="People" value={m.people} /><Stat label="Views" value={m.views} /></div>
        <DayChart days={m.by_day} windowDays={windowDays} since={since} />
        {privateNote && <p className="m-0 text-[12px] text-paper-faint">{privateNote}</p>}
      </section>
      <section aria-label="Who reads it" className={CARD}>
        <h3 className={H3}>Who reads it</h3>
        {bar.length > 0 && (
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-ink-600" aria-hidden="true">
            {bar.map((s) => <span key={s.key} className={SEG_COLOR[s.key]} style={{ width: `${s.pct}%` }} />)}
          </div>
        )}
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-paper-dim">
          {bar.length === 0 && <li>Nobody yet.</li>}
          {bar.map((s) => (
            <li key={s.key} className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 flex-none rounded-sm ${SEG_COLOR[s.key]}`} aria-hidden="true" />
              {s.label}: {s.count} ({s.pct}%)
            </li>
          ))}
        </ul>
      </section>
      <section aria-label="How well it reads" className={CARD}>
        <h3 className={H3}>How well it reads</h3>
        <div className="flex flex-wrap gap-5">
          <Stat label="Read half" value={pct(m.read_half_pct)} /><Stat label="Finished" value={pctOrDash(m.finished_pct)} />
          <Stat label="Typical time" value={formatDuration(m.median_seconds)} />
        </div>
        <p className="m-0 text-[12px] text-paper-faint">{TYPICAL_TIME_HINT}</p>
      </section>
      <section aria-label="Access and conversion" className={CARD}>
        <h3 className={H3}>Access &amp; conversion</h3>
        <div className="flex flex-wrap gap-5">
          {isMembers && <Stat label="Paywall views" value={m.paywall_views} />}
          {showFull && <Stat label="“Read the full article” clicks" value={m.read_full_clicks} />}
          <Stat label="Sign-ups after reading" value={m.signups_after} />
        </div>
      </section>
      <section aria-label="Where readers came from" className={CARD}>
        <h3 className={H3}>Where readers came from</h3>
        {m.channels.length === 0 && <p className="m-0 text-[13px] text-paper-dim">No visits recorded.</p>}
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-paper-dim">
          {m.channels.map((c) => <li key={c.channel} className="flex justify-between"><span>{channelLabel(c.channel)}</span><span>{c.visits}</span></li>)}
        </ul>
      </section>
      </>}
      {isMembers && (
        <section aria-label="Members' reads (all time)" className={CARD}>
          <h3 className={H3}>Members&apos; reads (all time)</h3>
          <div className="flex flex-wrap gap-5">
            <Stat label="Opens" value={m.member_reads.opens} /><Stat label="Read half" value={m.member_reads.read_half} />
            <Stat label="Finished" value={m.member_reads.finished} />
          </div>
        </section>
      )}
      <section aria-label="Google search" className={CARD}>
        <h3 className={H3}>Google search</h3>
        {m.search.impressions === 0 && m.search.clicks === 0
          ? <p className="m-0 text-[13px] text-paper-dim">{NO_SEARCH_TEXT}</p>
          : (
            <div className="flex flex-wrap gap-5">
              <Stat label="Impressions" value={m.search.impressions} /><Stat label="Clicks" value={m.search.clicks} />
              <Stat label="Average position" value={m.search.avg_position ?? '—'} />
            </div>
          )}
      </section>
    </>
  )
}

export function ReadersPanel({ pieceId, sectionLabel }: { pieceId: number; sectionLabel: string }) {
  const [days, setDays] = useState<Window>(30)
  const [which, setWhich] = useState<'free' | 'members'>('free')
  const q = useQuery({ queryKey: ['editorial-piece-metrics', pieceId, days], queryFn: () => getPieceMetrics(pieceId, days) })
  const me = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const canOpen = me.data?.desk_tabs?.includes('articles') === true
  return (
    <>
      <h2 className="m-0 mb-1.5 font-ui text-[15px] font-semibold text-paper">Readers</h2>
      <Switch label="Time window" items={WINDOWS.map((w) => ({ key: w, label: `${w} days` }))} value={days} onChange={setDays} />
      {q.isPending && <p role="status" className="m-0 mt-2 text-[13px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 mt-2 text-[13px] text-radar-red">Could not load readers. {describeError(q.error)}</p>}
      {q.data && <Body data={q.data} which={which} onWhich={setWhich} sectionLabel={sectionLabel} canOpen={canOpen} />}
    </>
  )
}

function Body({ data, which, onWhich, sectionLabel, canOpen }: {
  data: PieceMetrics; canOpen: boolean
  which: 'free' | 'members'; onWhich: (w: 'free' | 'members') => void; sectionLabel: string
}) {
  const since = countingSince(data.tracking_since)
  const note = noteOf(data)
  const quiet = windowEmpty(data)
  const many = data.copies.length > 1
  const copy = data.copies.find((c) => c.where === which) ?? data.copies[0]
  return (
    <>
      {many && <div className="mt-2"><Switch label="Which copy" items={data.copies.map((c) => ({ key: c.where, label: bothLabel(c.where, sectionLabel) }))} value={copy.where} onChange={onWhich} /></div>}
      {since && <p className="m-0 mt-2 text-[12px] text-paper-faint">{since}</p>}
      {note && <p role="status" className="m-0 mt-2 text-[13px] text-paper-dim">{note}</p>}
      {quiet && <p role="status" className="m-0 mt-2 text-[13px] text-paper-dim">{emptyText(data)}</p>}
      {!note && copy && <Cards copy={copy} many={many} windowDays={data.window_days} since={data.tracking_since} quiet={quiet} canOpen={canOpen} />}
    </>
  )
}
